<template>
    <div class="explanation-container">
        <div>
            <h3 class="text-h5 text-left mb-3">Order Changes Needed</h3>

            <p class="explanation-text mb-3">
                If {{ nickname }} had clicked "Request Changes", here's what would have happened:
            </p>

            <div class="explanation-points">
                <div
                    class="explanation-point"
                    v-for="(point, idx) in explanation_points"
                    :key="idx"
                >
                    <v-icon
                        color="primary"
                        size="16"
                        class="point-icon"
                        aria-hidden="true"
                    >
                        mdi-chevron-right
                    </v-icon>
                    <p class="point-text">{{ point }}</p>
                </div>
            </div>
        </div>

        <div class="contact-request-preview">
            <div class="preview-header">
                <v-icon
                    color="primary"
                    size="20"
                    aria-hidden="true"
                >
                    mdi-message-text
                </v-icon>
                <span class="preview-title">Preview: What Your Team Will See Once Submitted</span>
            </div>

            <div class="home-screen-notification">
                <div class="notification-card">
                    <div class="notification-label">
                        <span>Contact Requests</span>
                    </div>
                    <div class="notification-badge">
                        <div class="count-unread">1</div>
                    </div>
                </div>
            </div>

            <div class="flow-indicator">
                <v-icon
                    color="grey darken-1"
                    size="24"
                >
                    mdi-arrow-down
                </v-icon>
            </div>

            <div class="contact-request-card">
                <p class="card-title">New Contact Request</p>

                <div class="card-field">
                    <span class="field-label">Source</span>
                    <span class="field-value">Order Confirmation Change Request</span>
                </div>

                <div class="card-field">
                    <span class="field-label">Customer Name</span>
                    <span class="field-value">{{ change_request.first_name }} {{ change_request.last_name }}</span>
                </div>

                <div class="card-field">
                    <span class="field-label">Phone Number</span>
                    <span class="field-value">{{ change_request.phone }}</span>
                </div>

                <div class="card-field">
                    <span class="field-label">Order Reference</span>
                    <span class="field-value">{{ change_request.order_reference }}</span>
                </div>

                <div
                    v-if="display_message"
                    class="card-field"
                >
                    <span class="field-label">Message</span>
                    <span class="field-value message-value">{{ display_message }}</span>
                </div>

                <v-btn
                    color="primary"
                    small
                    class="reply-btn mt-2"
                    @click="$emit('reply-clicked')"
                >
                    <v-icon
                        left
                        small
                    >mdi-reply</v-icon>
                    Reply
                </v-btn>
            </div>
        </div>
    </div>
</template>

<script>
export default {
    props: {
        order_data: {
            type: Object,
            required: true
        },

        change_request: {
            type: Object,
            default: () => ({})
        }
    },

    computed: {
        nickname() {
            return this.order_data?.customer_nickname || 'The customer';
        },

        display_message() {
            return this.change_request.final_message || this.change_request.description || '';
        },

        explanation_points() {
            return [
                `She is directed to this form, which provides her the ability to change any of the previously-submitted order details. There's also an "Additional Notes" field for any further changes or context she wants to provide`,
                `She scrolls down and clicks the "Check All" button. Because changes are being requested and further communication will be needed, written consent is required to proceed`,
                `She clicks "Submit Change Request" to submit the form`
            ];
        }
    }
}
</script>

<style lang="scss" scoped>
.explanation-container {
    display: flex;
    flex-direction: column;
    justify-content: center;
    gap: 2rem;
    align-items: center;
    padding: 2rem;

    @media (max-width: 900px) {
        padding: .5rem 0;
    }
}

.contact-request-preview {
    width: 100%;
    max-width: 600px;

    .preview-header {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        margin-bottom: 0.75rem;

        .preview-title {
            font-weight: 600;
            font-size: 0.95rem;
            color: var(--v-primary-base);
        }
    }
}

.home-screen-notification {
    margin-bottom: 1rem;

    .notification-card {
        background: #fff;
        border: 1px solid #e0e0e0;
        border-bottom: 1px solid rgba(0, 0, 0, 0.1);
        border-radius: 4px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 10px 15px;
        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
        cursor: pointer;
        transition: background-color 0.2s;

        &:hover {
            background-color: #f8f9fa;
        }

        .notification-label {
            flex: 1;
            max-width: 265px;
            text-overflow: ellipsis;
            overflow: hidden;
            white-space: nowrap;

            span {
                font-size: 0.95rem;
                font-weight: 500;
                color: #333;
            }
        }

        .notification-badge {
            .count-unread {
                background-color: var(--v-primary-base);
                color: white;
                border-radius: 12px;
                padding: 2px 8px;
                font-size: 0.85rem;
                font-weight: 600;
                min-width: 20px;
                text-align: center;
            }
        }
    }
}

.flow-indicator {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.25rem;
    margin-bottom: 1rem;
}

.contact-request-card {
    background: #fff;
    border: 1px solid #e0e0e0;
    border-radius: 8px;
    padding: 1rem;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

    .card-title {
        text-align: center;
        font-size: 1rem;
        font-weight: 600;
        margin-bottom: 1rem;
        color: #333;
    }

    .card-field {
        margin-bottom: 0.75rem;

        .field-label {
            display: block;
            font-weight: 600;
            font-size: 0.85rem;
            color: #555;
            margin-bottom: 0.125rem;
        }

        .field-value {
            display: block;
            font-size: 0.9rem;
            color: #333;

            &.message-value {
                white-space: pre-wrap;
                background: #f8f9fa;
                padding: 0.5rem;
                border-radius: 4px;
                margin-top: 0.25rem;
            }
        }
    }

    .reply-btn {
        width: 100%;
    }
}
</style>
